import { useEffect, useRef, useState } from "react";
import { buildBookingWhatsApp, placeOf } from "../../lib/bookingMessage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORE = "pg_booking_wizard";
const EMPTY = {
  service: "", city: "", cityCustom: "", mode: "date", date: "", window: "",
  name: "", phone: "", email: "", item: "", notes: "", contact: "WhatsApp", consent: false,
};
const PHONE_RE = /^\+?[0-9][0-9\s\-()/]{5,19}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

const readStore = () => { try { return JSON.parse(sessionStorage.getItem(STORE) || "null"); } catch { return null; } };

const uploadPhotos = async (files) => {
  if (!files.length) return [];
  try {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    const res = await fetch(`${BACKEND_URL}/api/upload`, { method: "POST", body: fd });
    if (!res.ok) return [];
    const json = await res.json();
    return (json.urls || []).map((u) => (u.startsWith("http") ? u : `${BACKEND_URL}${u}`));
  } catch { return []; }
};

export const useBookingWizard = (c, lang) => {
  const [data, setData] = useState(EMPTY);
  const [step, setStep] = useState(0);
  const [photos, setPhotos] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(() => readStore()?.result || null);
  const reqId = useRef(readStore()?.reqId || null);

  const set = (k, v) => { setData((d) => ({ ...d, [k]: v })); setErrors({}); };

  useEffect(() => {
    const onCity = (e) => { if (e.detail?.city) { setData((d) => ({ ...d, city: e.detail.city, cityCustom: "" })); setErrors({}); } };
    window.addEventListener("puregloss:select-city", onCity);
    return () => window.removeEventListener("puregloss:select-city", onCity);
  }, []);

  const check = (s) => {
    const e = {}, E = c.errors;
    if (s === 0 && !data.service) e.service = E.service;
    if (s === 1 && !(data.city && (data.city !== "__other" || data.cityCustom.trim().length >= 2))) e.location = E.location;
    if (s === 2 && data.mode === "date" && !data.date) e.date = E.date;
    if (s === 3) {
      if (data.name.trim().length < 2) e.name = E.name;
      if (!PHONE_RE.test(data.phone.trim())) e.phone = E.phone;
      if (data.email.trim() && !EMAIL_RE.test(data.email.trim())) e.email = E.email;
      if (data.contact === "Email" && !data.email.trim()) e.email = E.emailRequired;
      if (photos.length > 5) e.photos = E.photos;
    }
    if (s === 4 && !data.consent) e.consent = E.consent;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (check(step)) setStep((s) => Math.min(4, s + 1)); };
  const back = () => { setErrors({}); setStep((s) => Math.max(0, s - 1)); };
  const goTo = (s) => { setErrors({}); setStep(s); };

  const submit = async (via) => {
    if (submitting || !check(4)) return;
    const win = via === "wa" ? window.open("", "_blank") : null;
    if (win) win.opener = null;
    setSubmitting(true);
    try {
      if (!reqId.current) reqId.current = newId();
      const photoUrls = await uploadPhotos(photos);
      const res = await fetch(`${BACKEND_URL}/api/quote/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: data.name.trim(), phone: data.phone.trim(), email: data.email.trim() || null,
          city: placeOf(data), service_type: c.services[data.service].title, service_key: data.service,
          car_model: data.item.trim() || null, message: data.notes.trim() || null, language: lang,
          photo_urls: photoUrls, preferred_contact: data.contact, consent_gdpr: true,
          availability: data.mode, preferred_date: data.mode === "date" ? data.date : null,
          time_window: data.mode === "earliest" ? null : data.window || null,
          client_request_id: reqId.current, source: "wizard",
        }),
      });
      if (!res.ok) throw new Error("submit failed");
      const { id } = await res.json();
      const waUrl = buildBookingWhatsApp(data, c, lang, { id, photoCount: photoUrls.length });
      const r = { id, via, waUrl };
      setResult(r);
      sessionStorage.setItem(STORE, JSON.stringify({ result: r, reqId: reqId.current }));
      if (via === "wa") { if (win) win.location.href = waUrl; else window.location.href = waUrl; }
    } catch {
      if (win) win.close();
      setErrors({ submit: c.errors.submit });
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    sessionStorage.removeItem(STORE);
    reqId.current = null;
    setData(EMPTY); setPhotos([]); setErrors({}); setResult(null); setStep(0);
  };

  return { data, set, step, next, back, goTo, photos, setPhotos, errors, submitting, submit, result, reset };
};
